package com.sweetlime.dungeonamigo.util

import dungeonamigo.shared.generated.resources.*
import org.jetbrains.compose.resources.DrawableResource

object AvatarMapper {

    val races = listOf("human", "elf", "dwarf", "halfling", "gnome", "half_orc", "tiefling", "dragonborn")
    val classes = listOf(
        "barbarian", "bard", "cleric", "druid", "fighter", "monk",
        "paladin", "ranger", "rogue", "sorcerer", "warlock", "wizard",
    )

    // Amigo+ variants — ordered for display (2 masc, 2 fem, 2 androgynous)
    val variants = listOf(
        "masculine_a", "masculine_b",
        "feminine_a", "feminine_b",
        "androgynous_a", "androgynous_b",
    )

    val raceDisplayNames = mapOf(
        "human"      to "Human",
        "elf"        to "Elf",
        "dwarf"      to "Dwarf",
        "halfling"   to "Halfling",
        "gnome"      to "Gnome",
        "half_orc"   to "Half-Orc",
        "tiefling"   to "Tiefling",
        "dragonborn" to "Dragonborn",
    )

    val variantLabels = mapOf(
        "masculine_a"   to "Masculine I",
        "masculine_b"   to "Masculine II",
        "feminine_a"    to "Feminine I",
        "feminine_b"    to "Feminine II",
        "androgynous_a" to "Androgynous I",
        "androgynous_b" to "Androgynous II",
    )

    val allAvatarIds: List<String> =
        races.flatMap { race -> classes.map { cls -> "${race}_${cls}" } }

    /** Builds the base avatarId from race and class display names. Returns "" if unknown. */
    fun avatarId(race: String, characterClass: String): String {
        val r = raceKey(race)
        val c = classKey(characterClass)
        return if (r.isNotBlank() && c.isNotBlank()) "${r}_${c}" else ""
    }

    /** Builds an avatarId that includes a specific variant suffix. */
    fun avatarIdWithVariant(race: String, characterClass: String, variant: String): String {
        val base = avatarId(race, characterClass)
        return if (base.isNotBlank() && variant.isNotBlank()) "${base}_${variant}" else base
    }

    /** Strips any trailing variant suffix and returns the base "race_class" ID. */
    fun baseAvatarId(avatarId: String): String {
        for (v in variants) {
            if (avatarId.endsWith("_$v")) return avatarId.removeSuffix("_$v")
        }
        return avatarId
    }

    /** Returns the variant suffix if the avatarId contains one, otherwise "". */
    fun variantOf(avatarId: String): String {
        for (v in variants) {
            if (avatarId.endsWith("_$v")) return v
        }
        return ""
    }

    fun raceKey(race: String): String = when (race.lowercase().trim()) {
        "human"                  -> "human"
        "elf"                    -> "elf"
        "dwarf"                  -> "dwarf"
        "halfling"               -> "halfling"
        "gnome"                  -> "gnome"
        "half-orc", "half orc"   -> "half_orc"
        "tiefling"               -> "tiefling"
        "dragonborn"             -> "dragonborn"
        else                     -> ""
    }

    fun classKey(characterClass: String): String = when (characterClass.lowercase().trim()) {
        "barbarian" -> "barbarian"
        "bard"      -> "bard"
        "cleric"    -> "cleric"
        "druid"     -> "druid"
        "fighter"   -> "fighter"
        "monk"      -> "monk"
        "paladin"   -> "paladin"
        "ranger"    -> "ranger"
        "rogue"     -> "rogue"
        "sorcerer"  -> "sorcerer"
        "warlock"   -> "warlock"
        "wizard"    -> "wizard"
        else        -> ""
    }

    /**
     * Resolves an avatarId to a DrawableResource.
     * Tries the exact ID first (handles future variant PNGs), then falls back to
     * the base race_class image. Returns null if the base is also unknown.
     */
    fun drawableFor(avatarId: String): DrawableResource? {
        if (avatarId.isBlank()) return null
        return drawableForId(avatarId) ?: drawableForId(baseAvatarId(avatarId))
    }

    private fun drawableForId(id: String): DrawableResource? = when (id) {
        // ── Human ─────────────────────────────────────────────────────────────
        "human_barbarian" -> Res.drawable.avatar_human_barbarian
        "human_bard"      -> Res.drawable.avatar_human_bard
        "human_cleric"    -> Res.drawable.avatar_human_cleric
        "human_druid"     -> Res.drawable.avatar_human_druid
        "human_fighter"   -> Res.drawable.avatar_human_fighter
        "human_monk"      -> Res.drawable.avatar_human_monk
        "human_paladin"   -> Res.drawable.avatar_human_paladin
        "human_ranger"    -> Res.drawable.avatar_human_ranger
        "human_rogue"     -> Res.drawable.avatar_human_rogue
        "human_sorcerer"  -> Res.drawable.avatar_human_sorcerer
        "human_warlock"   -> Res.drawable.avatar_human_warlock
        "human_wizard"    -> Res.drawable.avatar_human_wizard
        // ── Elf ───────────────────────────────────────────────────────────────
        "elf_barbarian"   -> Res.drawable.avatar_elf_barbarian
        "elf_bard"        -> Res.drawable.avatar_elf_bard
        "elf_cleric"      -> Res.drawable.avatar_elf_cleric
        "elf_druid"       -> Res.drawable.avatar_elf_druid
        "elf_fighter"     -> Res.drawable.avatar_elf_fighter
        "elf_monk"        -> Res.drawable.avatar_elf_monk
        "elf_paladin"     -> Res.drawable.avatar_elf_paladin
        "elf_ranger"      -> Res.drawable.avatar_elf_ranger
        "elf_rogue"       -> Res.drawable.avatar_elf_rogue
        "elf_sorcerer"    -> Res.drawable.avatar_elf_sorcerer
        "elf_warlock"     -> Res.drawable.avatar_elf_warlock
        "elf_wizard"      -> Res.drawable.avatar_elf_wizard
        // ── Dwarf ─────────────────────────────────────────────────────────────
        "dwarf_barbarian" -> Res.drawable.avatar_dwarf_barbarian
        "dwarf_bard"      -> Res.drawable.avatar_dwarf_bard
        "dwarf_cleric"    -> Res.drawable.avatar_dwarf_cleric
        "dwarf_druid"     -> Res.drawable.avatar_dwarf_druid
        "dwarf_fighter"   -> Res.drawable.avatar_dwarf_fighter
        "dwarf_monk"      -> Res.drawable.avatar_dwarf_monk
        "dwarf_paladin"   -> Res.drawable.avatar_dwarf_paladin
        "dwarf_ranger"    -> Res.drawable.avatar_dwarf_ranger
        "dwarf_rogue"     -> Res.drawable.avatar_dwarf_rogue
        "dwarf_sorcerer"  -> Res.drawable.avatar_dwarf_sorcerer
        "dwarf_warlock"   -> Res.drawable.avatar_dwarf_warlock
        "dwarf_wizard"    -> Res.drawable.avatar_dwarf_wizard
        // ── Halfling ──────────────────────────────────────────────────────────
        "halfling_barbarian" -> Res.drawable.avatar_halfling_barbarian
        "halfling_bard"      -> Res.drawable.avatar_halfling_bard
        "halfling_cleric"    -> Res.drawable.avatar_halfling_cleric
        "halfling_druid"     -> Res.drawable.avatar_halfling_druid
        "halfling_fighter"   -> Res.drawable.avatar_halfling_fighter
        "halfling_monk"      -> Res.drawable.avatar_halfling_monk
        "halfling_paladin"   -> Res.drawable.avatar_halfling_paladin
        "halfling_ranger"    -> Res.drawable.avatar_halfling_ranger
        "halfling_rogue"     -> Res.drawable.avatar_halfling_rogue
        "halfling_sorcerer"  -> Res.drawable.avatar_halfling_sorcerer
        "halfling_warlock"   -> Res.drawable.avatar_halfling_warlock
        "halfling_wizard"    -> Res.drawable.avatar_halfling_wizard
        // ── Gnome ─────────────────────────────────────────────────────────────
        "gnome_barbarian"    -> Res.drawable.avatar_gnome_barbarian
        "gnome_bard"         -> Res.drawable.avatar_gnome_bard
        "gnome_cleric"       -> Res.drawable.avatar_gnome_cleric
        "gnome_druid"        -> Res.drawable.avatar_gnome_druid
        "gnome_fighter"      -> Res.drawable.avatar_gnome_fighter
        "gnome_monk"         -> Res.drawable.avatar_gnome_monk
        "gnome_paladin"      -> Res.drawable.avatar_gnome_paladin
        "gnome_ranger"       -> Res.drawable.avatar_gnome_ranger
        "gnome_rogue"        -> Res.drawable.avatar_gnome_rogue
        "gnome_sorcerer"     -> Res.drawable.avatar_gnome_sorcerer
        "gnome_warlock"      -> Res.drawable.avatar_gnome_warlock
        "gnome_wizard"       -> Res.drawable.avatar_gnome_wizard
        // ── Half-Orc ──────────────────────────────────────────────────────────
        "half_orc_barbarian" -> Res.drawable.avatar_half_orc_barbarian
        "half_orc_bard"      -> Res.drawable.avatar_half_orc_bard
        "half_orc_cleric"    -> Res.drawable.avatar_half_orc_cleric
        "half_orc_druid"     -> Res.drawable.avatar_half_orc_druid
        "half_orc_fighter"   -> Res.drawable.avatar_half_orc_fighter
        "half_orc_monk"      -> Res.drawable.avatar_half_orc_monk
        "half_orc_paladin"   -> Res.drawable.avatar_half_orc_paladin
        "half_orc_ranger"    -> Res.drawable.avatar_half_orc_ranger
        "half_orc_rogue"     -> Res.drawable.avatar_half_orc_rogue
        "half_orc_sorcerer"  -> Res.drawable.avatar_half_orc_sorcerer
        "half_orc_warlock"   -> Res.drawable.avatar_half_orc_warlock
        "half_orc_wizard"    -> Res.drawable.avatar_half_orc_wizard
        // ── Tiefling ──────────────────────────────────────────────────────────
        "tiefling_barbarian" -> Res.drawable.avatar_tiefling_barbarian
        "tiefling_bard"      -> Res.drawable.avatar_tiefling_bard
        "tiefling_cleric"    -> Res.drawable.avatar_tiefling_cleric
        "tiefling_druid"     -> Res.drawable.avatar_tiefling_druid
        "tiefling_fighter"   -> Res.drawable.avatar_tiefling_fighter
        "tiefling_monk"      -> Res.drawable.avatar_tiefling_monk
        "tiefling_paladin"   -> Res.drawable.avatar_tiefling_paladin
        "tiefling_ranger"    -> Res.drawable.avatar_tiefling_ranger
        "tiefling_rogue"     -> Res.drawable.avatar_tiefling_rogue
        "tiefling_sorcerer"  -> Res.drawable.avatar_tiefling_sorcerer
        "tiefling_warlock"   -> Res.drawable.avatar_tiefling_warlock
        "tiefling_wizard"    -> Res.drawable.avatar_tiefling_wizard
        // ── Dragonborn ────────────────────────────────────────────────────────
        "dragonborn_barbarian" -> Res.drawable.avatar_dragonborn_barbarian
        "dragonborn_bard"      -> Res.drawable.avatar_dragonborn_bard
        "dragonborn_cleric"    -> Res.drawable.avatar_dragonborn_cleric
        "dragonborn_druid"     -> Res.drawable.avatar_dragonborn_druid
        "dragonborn_fighter"   -> Res.drawable.avatar_dragonborn_fighter
        "dragonborn_monk"      -> Res.drawable.avatar_dragonborn_monk
        "dragonborn_paladin"   -> Res.drawable.avatar_dragonborn_paladin
        "dragonborn_ranger"    -> Res.drawable.avatar_dragonborn_ranger
        "dragonborn_rogue"     -> Res.drawable.avatar_dragonborn_rogue
        "dragonborn_sorcerer"  -> Res.drawable.avatar_dragonborn_sorcerer
        "dragonborn_warlock"   -> Res.drawable.avatar_dragonborn_warlock
        "dragonborn_wizard"    -> Res.drawable.avatar_dragonborn_wizard
        // ── Unknown / variants not yet generated ──────────────────────────────
        else -> null
    }
}